import { useSyncExternalStore } from 'react'
import { apiGet } from './client'

/**
 * Which card stores the buy links may point at, as decided by the API.
 *
 * WHY THE SERVER DECIDES. Card Kingdom ships dark: its links exist in this
 * build but must not be offered until the affiliate account is linked. The
 * API's `GET /stores` answers with the enabled set, so turning a store on or
 * off is an API variable change and never a web deploy.
 *
 * Fetched at most once per page load (a module-level promise), kicked off
 * from main.tsx. Until it resolves, and on ANY failure, the enabled set is
 * the two stores that were live before `/stores` existed, so a slow or broken
 * API can never hide a store users already had, nor surface one that is dark.
 */

/** The regional stores (Japan, Europe, Brazil). Plain links, never affiliate
 *  links, and each ships dark behind its own API flag: like Card Kingdom, a
 *  regional id is offered only once `/stores` lists it. */
export type RegionalStoreId = 'hareruya' | 'cardmarket' | 'ligamagic'

export type StoreId = 'tcgplayer' | 'manapool' | 'cardkingdom' | RegionalStoreId

/** Every store id this client knows how to link to. Anything else the server
 *  sends is ignored, since there would be no link to build for it. */
export const KNOWN_STORES: readonly StoreId[] = [
  'tcgplayer', 'manapool', 'cardkingdom', 'hareruya', 'cardmarket', 'ligamagic',
]

/** The stores a visitor is defaulted to by inferred region (utils/region.ts). */
export const REGIONAL_STORES: readonly RegionalStoreId[] = ['hareruya', 'cardmarket', 'ligamagic']

export function isRegionalStore(v: StoreId | null | undefined): v is RegionalStoreId {
  return v != null && (REGIONAL_STORES as readonly string[]).includes(v)
}

/** The enabled set before `/stores` answers and whenever it cannot be used. */
export const FALLBACK_STORES: readonly StoreId[] = ['tcgplayer', 'manapool']

function isStoreId(v: unknown): v is StoreId {
  return typeof v === 'string' && (KNOWN_STORES as readonly string[]).includes(v)
}

/**
 * Validate a `/stores` body. Parsed from `unknown` because it is a wire
 * contract: a missing or wrong-typed `stores` field, or a list naming no
 * store this client knows, is unusable and yields the fallback. Otherwise
 * unknown ids are dropped, duplicates collapse keeping first-seen order, and
 * TCGPlayer is always present (prepended if the server left it out), because
 * it is the store every other vendor falls back to.
 */
export function parseStores(body: unknown): readonly StoreId[] {
  if (typeof body !== 'object' || body === null) return FALLBACK_STORES
  const raw = (body as { stores?: unknown }).stores
  if (!Array.isArray(raw)) return FALLBACK_STORES
  const out: StoreId[] = []
  for (const v of raw) {
    if (isStoreId(v) && !out.includes(v)) out.push(v)
  }
  if (out.length === 0) return FALLBACK_STORES
  // eslint-disable-next-line i18next/no-literal-string -- a code compared, not shown
  if (!out.includes('tcgplayer')) out.unshift('tcgplayer')
  return out
}

let enabled: readonly StoreId[] = FALLBACK_STORES
let inflight: Promise<readonly StoreId[]> | null = null
const listeners = new Set<() => void>()

/**
 * Fetch the enabled stores once per page load. Repeat calls share the first
 * promise. Never rejects: every failure resolves to the fallback.
 */
export function loadStores(): Promise<readonly StoreId[]> {
  if (!inflight) {
    inflight = apiGet<unknown>('/stores')
      .then(parseStores, () => FALLBACK_STORES)
      .then(stores => {
        enabled = stores
        listeners.forEach(l => l())
        return stores
      })
  }
  return inflight
}

/** The current enabled set (the fallback until `loadStores` resolves). */
export function getEnabledStores(): readonly StoreId[] {
  return enabled
}

export function subscribeStores(cb: () => void): () => void {
  listeners.add(cb)
  return () => { listeners.delete(cb) }
}

/** Reactive enabled-store list; re-renders when `/stores` resolves. */
export function useEnabledStores(): readonly StoreId[] {
  return useSyncExternalStore(subscribeStores, getEnabledStores, () => FALLBACK_STORES)
}

/** Test-only: forget the cached promise and return to the fallback. */
export function __resetStoresForTests(): void {
  enabled = FALLBACK_STORES
  inflight = null
  listeners.forEach(l => l())
}
